import {IImageManualModel, IImageModel, IManualFinderModel, IManualItemModel} from "../../types/manualFinder";
import {mapManualItemToManual} from "../../utils/modelBuilder";
import {API} from "aws-amplify";
import {GRAPHQL_AUTH_MODE, GraphQLQuery, GraphQLSubscription} from "@aws-amplify/api";
import {
    ExtractSpecialToolsQuery,
    ExtractSpecialToolsQueryVariables,
    OnCalculationFinishedSubscription,
    OnspecialToolsExtractionFinishedSubscription
} from "../../API";
import * as queries from "../../graphql/queries";
import * as subscriptions from "../../graphql/subscriptions";
import {v4 as uuidv4} from "uuid";

export interface ISpecialToolMatchPayload {
    topK: number;
    maxNumberOfManuals: number;
    image_threshold: number;
    itemMp: string;
    fileIds: string;
    filter: any;
}

export const extractToolAndManualsFromPDF = async (payload: ISpecialToolMatchPayload): Promise<IManualFinderModel> => {
    try {
        const subscriptionId = uuidv4();

        const queryResult = await fetchSpecialToolsData(payload, subscriptionId);
        if (!queryResult) {
            return {specialTools: [], manuals: [], extractedImages: [], manualsBasedOnImages: []} as IManualFinderModel;
        }

        const [{specialTools, manuals}, {extractedImages, manualsBasedOnImages}] = await Promise.all([
            subscribeToSpecialToolsExtraction(subscriptionId),
            subscribeToImageExtraction(subscriptionId),
        ]);
        const remapManuals = manuals.map((manualItem) => {
            return mapManualItemToManual(manualItem);
        })
        return {
            specialTools: specialTools,
            manuals: remapManuals,
            extractedImages: extractedImages,
            manualsBasedOnImages: manualsBasedOnImages
        } as IManualFinderModel;
    } catch (error) {
        throw error;
    }
};

const fetchSpecialToolsData = async (
    payload: ISpecialToolMatchPayload,
    subscriptionId: string
): Promise<any> => {
    try {
        const queryResult = await API.graphql<GraphQLQuery<ExtractSpecialToolsQuery>>({
            query: queries.extractSpecialTools,
            variables: {
                pdfIds: payload.fileIds,
                topK: payload.topK,
                itemMp: payload.itemMp,
                subscriptionId,
                image_threshold: payload.image_threshold,
                filter: JSON.stringify(payload.filter),
                maxNumberOfManuals: payload.maxNumberOfManuals
            } as ExtractSpecialToolsQueryVariables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });

        return queryResult?.data?.extractSpecialTools
            ? JSON.parse(queryResult.data.extractSpecialTools)
            : null;
    } catch (error) {
        throw error;
    }
};

const subscribeToSpecialToolsExtraction = (
    subscriptionId: string
): Promise<{ specialTools: string[], manuals: IManualItemModel[] }> => {
    return new Promise((resolve, reject) => {
        const subscription = API.graphql<
            GraphQLSubscription<OnspecialToolsExtractionFinishedSubscription>
        >({
            query: subscriptions.onspecialToolsExtractionFinished,
            variables: {id: subscriptionId},
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        }).subscribe({
            next: (event: any) => {
                const response = event.value.data?.onspecialToolsExtractionFinished.results;
                if (!response) return;
                const {specialTools, manuals} = JSON.parse(response);
                const remapSpecialTools: string[] = JSON.parse(specialTools);
                const remapManuals: IManualItemModel[] = JSON.parse(manuals);
                resolve({specialTools: remapSpecialTools, manuals: remapManuals});
                subscription.unsubscribe();
            },
            error: (error: any) => {
                reject(error);
                subscription.unsubscribe();
            },
        });
    });
};

const subscribeToImageExtraction = (
    subscriptionId: string
): Promise<{ extractedImages: Record<string, IImageModel>[], manualsBasedOnImages: IImageManualModel[] }> => {
    return new Promise((resolve, reject) => {
        const subscription = API.graphql<GraphQLSubscription<OnCalculationFinishedSubscription>>({
            query: subscriptions.onCalculationFinished,
            variables: {id: subscriptionId},
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        }).subscribe({
            next: (event: any) => {
                const response = event.value.data?.onCalculationFinished.results;
                if (!response) return;
                const {pinecone_results, manuals} = JSON.parse(response);
                const extractedImages: Record<string, IImageModel>[] = pinecone_results;
                const manualsBasedOnImages: IImageManualModel[] = manuals;
                resolve({extractedImages, manualsBasedOnImages});
                subscription.unsubscribe();
            },
            error: (error: any) => {
                reject(error);
                subscription.unsubscribe();
            },
        });
    });
};
